import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTableQueryParams, NzTableSortOrder } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { Subscription } from 'rxjs';
import {
  HospitalityAiContext,
  HospitalityAiMetricsResponse,
  HospitalityAiService,
  HospitalityQaEntryResponse,
  HospitalityQaPayload,
  HospitalityAiRecentEntry,
} from '../services/hospitality-ai.service';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-hospitality-ai-insights',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzCardModule,
    NzGridModule,
    NzTableModule,
    NzTagModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzButtonModule,
    NzSpinModule,
    NzSwitchModule,
    NzIconModule,
    NzPopconfirmModule,
    NzAlertModule,
    NzDividerModule,
    NzToolTipModule,
    NzMessageModule,
  ],
  templateUrl: './hospitality-ai-insights.component.html',
  styleUrl: './hospitality-ai-insights.component.less',
})
export class HospitalityAiInsightsComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  private readonly defaultMetricsRangeDays = 30;
  private static readonly DOWNVOTES_DEFAULT_STATE: DownvoteQueryState = {
    page: 1,
    pageSize: 10,
    sortField: 'createdAt',
    sortOrder: 'desc',
  };
  private static readonly TURNS_DEFAULT_STATE: TurnQueryState = {
    page: 1,
    pageSize: 10,
    sortField: 'createdAt',
    sortOrder: 'desc',
  };
  private static readonly QA_DEFAULT_SORT: QaSortState = {
    sortField: 'updatedAt',
    sortOrder: 'desc',
  };

  loadingContext = false;
  loadingMetrics = false;
  loadingEntries = false;
  savingEntry = false;

  context?: HospitalityAiContext;
  metrics?: HospitalityAiMetricsResponse;

  readonly canFilterCompanies: boolean;
  private defaultCompanyId?: string;

  filtersForm: FormGroup;

  entryForm: FormGroup;

  pagination = {
    page: 1,
    pageSize: 10,
    total: 0,
  };

  downvotesQuery: DownvoteQueryState = { ...HospitalityAiInsightsComponent.DOWNVOTES_DEFAULT_STATE };
  turnsQuery: TurnQueryState = { ...HospitalityAiInsightsComponent.TURNS_DEFAULT_STATE };
  qaSort: QaSortState = { ...HospitalityAiInsightsComponent.QA_DEFAULT_SORT };

  entries: HospitalityQaEntryResponse[] = [];
  searchTerm = '';
  editingEntryId?: string;
  prefillAlertMessage = '';
  prefillAlertVisible = false;

  @ViewChild('qaFormSection', { read: ElementRef }) qaFormSection?: ElementRef<HTMLElement>;

  get companyOptions(): HospitalityAiContext['companies'] {
    return this.context?.companies ?? [];
  }

  get languageOptions(): HospitalityAiContext['languages'] {
    return this.context?.languages ?? [];
  }

  constructor(
    private readonly hospitalityAiService: HospitalityAiService,
    private readonly fb: FormBuilder,
    private readonly message: NzMessageService,
    private readonly translate: TranslateService,
    private readonly authService: AuthService,
  ) {
    this.canFilterCompanies = this.authService.isCurrentUserAdmin();
    this.filtersForm = this.fb.group({
      companyId: [''],
      propertyId: [''],
      language: [''],
      includeInactive: [false],
    });

    this.entryForm = this.fb.group({
      companyId: ['', Validators.required],
      propertyId: [''],
      language: [''],
      question: ['', [Validators.required, Validators.maxLength(2000)]],
      answer: ['', Validators.required],
      tags: [''],
      source: [''],
      isActive: [true],
    });
    this.subscriptions.add(
      this.filtersForm.get('companyId')!.valueChanges.subscribe(() => {
        this.filtersForm.get('propertyId')!.setValue('');
      }),
    );

    this.subscriptions.add(
      this.entryForm.get('companyId')!.valueChanges.subscribe(() => {
        this.entryForm.get('propertyId')!.setValue('');
      }),
    );
  }

  ngOnInit(): void {
    this.loadContext();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isEditing(): boolean {
    return !!this.editingEntryId;
  }

  get propertyOptionsForFilters(): Array<{ id: string; address: string }> {
    const companyId = this.filtersForm.value.companyId;
    if (!companyId) {
      return this.context?.companies.flatMap((company) => company.properties) ?? [];
    }
    return this.context?.companies.find((company) => company.id === companyId)?.properties ?? [];
  }

  get propertyOptionsForEntry(): Array<{ id: string; address: string }> {
    const companyId = this.entryForm.value.companyId;
    if (!companyId) {
      return [];
    }
    return this.context?.companies.find((company) => company.id === companyId)?.properties ?? [];
  }

  formatHelpfulRate(rate: number | null | undefined): string {
    if (rate === null || rate === undefined) {
      return '—';
    }
    return `${Math.round(rate * 100)}%`;
  }

  formatVotes(value: number | undefined): string {
    if (!value) {
      return '0';
    }
    return value.toLocaleString();
  }

  private loadContext(): void {
    this.loadingContext = true;
    this.hospitalityAiService.getContext().subscribe({
      next: (context) => {
        this.context = context;
        if (!this.canFilterCompanies) {
          this.defaultCompanyId = context.companies[0]?.id;
          if (this.defaultCompanyId) {
            this.filtersForm.patchValue({ companyId: this.defaultCompanyId });
            this.entryForm.patchValue({ companyId: this.defaultCompanyId });
          }
        }
        this.loadingContext = false;
        this.loadMetrics();
        this.loadEntries();
      },
      error: (error) => {
        console.error('Failed to load hospitality AI context', error);
        this.message.error(this.translate.instant('HOSPITALITY_AI.ERRORS.LOAD_CONTEXT'));
        this.loadingContext = false;
      },
    });
  }

  loadMetrics(): void {
    this.loadingMetrics = true;
    const { companyId, propertyId } = this.filtersForm.value;
    this.hospitalityAiService
      .getMetrics({
        companyId: companyId || undefined,
        propertyId: propertyId || undefined,
        days: this.defaultMetricsRangeDays,
        downvotesPage: this.downvotesQuery.page,
        downvotesPageSize: this.downvotesQuery.pageSize,
        downvotesSortField: this.downvotesQuery.sortField,
        downvotesSortOrder: this.downvotesQuery.sortOrder,
        turnsPage: this.turnsQuery.page,
        turnsPageSize: this.turnsQuery.pageSize,
        turnsSortField: this.turnsQuery.sortField,
        turnsSortOrder: this.turnsQuery.sortOrder,
      })
      .subscribe({
        next: (metrics) => {
          this.metrics = metrics;
          if (metrics.recent?.downvotes) {
            this.downvotesQuery = {
              page: metrics.recent.downvotes.page,
              pageSize: metrics.recent.downvotes.pageSize,
              sortField: metrics.recent.downvotes.sort.field as DownvoteSortField,
              sortOrder: metrics.recent.downvotes.sort.order as SortOrder,
            };
          }
          if (metrics.recent?.turns) {
            this.turnsQuery = {
              page: metrics.recent.turns.page,
              pageSize: metrics.recent.turns.pageSize,
              sortField: metrics.recent.turns.sort.field as TurnSortField,
              sortOrder: metrics.recent.turns.sort.order as SortOrder,
            };
          }
          this.loadingMetrics = false;
        },
        error: (error) => {
          console.error('Failed to load hospitality AI metrics', error);
          this.message.error(this.translate.instant('HOSPITALITY_AI.ERRORS.LOAD_METRICS'));
          this.loadingMetrics = false;
        },
      });
  }

  loadEntries(): void {
    this.loadingEntries = true;
    const { companyId, propertyId, language, includeInactive } = this.filtersForm.value;
    this.hospitalityAiService
      .listQaEntries({
        companyId: companyId || undefined,
        propertyId: propertyId || undefined,
        language: language || undefined,
        includeInactive: !!includeInactive,
        page: this.pagination.page,
        pageSize: this.pagination.pageSize,
        search: this.searchTerm.trim() || undefined,
        sortField: this.qaSort.sortField,
        sortOrder: this.qaSort.sortOrder,
      })
      .subscribe({
        next: (response) => {
          this.entries = response.items;
          this.pagination.total = response.total;
          this.loadingEntries = false;
        },
        error: (error) => {
          console.error('Failed to load hospitality QA entries', error);
          this.message.error(this.translate.instant('HOSPITALITY_AI.ERRORS.LOAD_QA'));
          this.loadingEntries = false;
        },
      });
  }

  applyFilters(): void {
    this.pagination.page = 1;
    this.prefillAlertVisible = false;
    this.prefillAlertMessage = '';
    this.resetRecentTables();
    this.resetQaSort();
    this.loadMetrics();
    this.loadEntries();
  }

  resetFilters(): void {
    this.filtersForm.reset({
      companyId: this.canFilterCompanies ? '' : this.defaultCompanyId || '',
      propertyId: '',
      language: '',
      includeInactive: false,
    });
    this.searchTerm = '';
    this.pagination.page = 1;
    this.prefillAlertVisible = false;
    this.prefillAlertMessage = '';
    this.resetRecentTables();
    this.resetQaSort();
    this.loadMetrics();
    this.loadEntries();
  }

  onSearch(): void {
    this.pagination.page = 1;
    this.loadEntries();
  }

  onEntriesQueryParamsChange(params: NzTableQueryParams): void {
    const { pageIndex, pageSize, sort } = params;
    let shouldReload = false;

    if (pageSize !== this.pagination.pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.page = 1;
      shouldReload = true;
    } else if (pageIndex !== this.pagination.page) {
      this.pagination.page = pageIndex;
      shouldReload = true;
    }

    const activeSort = sort.find((item) => item.value !== null);
    if (activeSort && activeSort.value) {
      const sortField = (activeSort.key as QaSortField) || HospitalityAiInsightsComponent.QA_DEFAULT_SORT.sortField;
      const sortOrder = activeSort.value === 'ascend' ? 'asc' : 'desc';
      if (sortField !== this.qaSort.sortField || sortOrder !== this.qaSort.sortOrder) {
        this.qaSort = {
          sortField,
          sortOrder,
        };
        this.pagination.page = 1;
        shouldReload = true;
      }
    } else if (
      this.qaSort.sortField !== HospitalityAiInsightsComponent.QA_DEFAULT_SORT.sortField ||
      this.qaSort.sortOrder !== HospitalityAiInsightsComponent.QA_DEFAULT_SORT.sortOrder
    ) {
      this.resetQaSort();
      this.pagination.page = 1;
      shouldReload = true;
    }

    if (shouldReload) {
      this.loadEntries();
    }
  }

  startCreate(): void {
    this.editingEntryId = undefined;
    this.prefillAlertMessage = '';
    this.prefillAlertVisible = false;
    this.entryForm.reset({
      companyId: this.canFilterCompanies ? '' : this.defaultCompanyId || '',
      propertyId: '',
      language: '',
      question: '',
      answer: '',
      tags: '',
      source: '',
      isActive: true,
    });
  }

  startEdit(entry: HospitalityQaEntryResponse): void {
    this.editingEntryId = entry.id;
    this.entryForm.reset({
      companyId: entry.companyId || '',
      propertyId: entry.propertyId || '',
      language: entry.language || '',
      question: entry.question,
      answer: entry.answer,
      tags: entry.tags?.join(', ') || '',
      source: entry.source || '',
      isActive: entry.isActive,
    });
  }

  cancelEdit(): void {
    this.startCreate();
  }

  prefillEntryFromDownvote(vote: HospitalityAiRecentEntry): void {
    const questionText = (vote.prompt || '').trim();
    const answerText = (vote.response || '').trim();
    if (!questionText && !answerText) {
      this.message.warning(this.translate.instant('HOSPITALITY_AI.RECENT.NO_MESSAGE_WARNING'));
      return;
    }

    this.startCreate();

    const targetCompanyId = vote.companyId || this.entryForm.value.companyId || this.defaultCompanyId || '';
    if (targetCompanyId) {
      this.entryForm.get('companyId')!.setValue(targetCompanyId);
    }

    const questionValue = questionText || answerText;

    this.entryForm.patchValue({
      question: questionValue,
      answer: answerText,
      language: vote.language || '',
      source: '',
      tags: '',
      isActive: true,
    });

    const propertyId = vote.propertyId || vote.property?.id;
    if (propertyId) {
      setTimeout(() => {
        this.entryForm.patchValue({ propertyId });
      });
    }

    const preview = this.createMessagePreview(questionValue);
    this.prefillAlertMessage = this.translate.instant('HOSPITALITY_AI.RECENT.PREFILL_ALERT_DESCRIPTION', {
      question: preview,
    });
    this.prefillAlertVisible = true;
    this.message.info(this.translate.instant('HOSPITALITY_AI.RECENT.PREFILL_TOAST'));
    setTimeout(() => {
      this.qaFormSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  submitEntry(): void {
    if (this.entryForm.invalid) {
      this.entryForm.markAllAsTouched();
      return;
    }

    const value = this.entryForm.value;
    const payload: HospitalityQaPayload = {
      companyId: value.companyId!,
      propertyId: value.propertyId || undefined,
      language: value.language || undefined,
      question: (value.question || '').trim(),
      answer: (value.answer || '').trim(),
      tags: value.tags
        ? value.tags
            .split(',')
            .map((tag: string) => tag.trim())
            .filter((tag: string) => !!tag)
        : undefined,
      source: value.source?.trim() || undefined,
      isActive: value.isActive ?? true,
    };

    this.savingEntry = true;
    const request$ = this.editingEntryId
      ? this.hospitalityAiService.updateQaEntry(this.editingEntryId, payload)
      : this.hospitalityAiService.createQaEntry(payload);

    request$.subscribe({
      next: () => {
        this.message.success(
          this.translate.instant(
            this.editingEntryId
              ? 'HOSPITALITY_AI.QA_SECTION.UPDATE_SUCCESS'
              : 'HOSPITALITY_AI.QA_SECTION.SAVE_SUCCESS',
          ),
        );
        this.savingEntry = false;
        this.startCreate();
        this.pagination.page = 1;
        this.resetRecentTables();
        this.loadEntries();
        this.loadMetrics();
      },
      error: (error) => {
        console.error('Failed to save hospitality QA entry', error);
        this.message.error(this.translate.instant('HOSPITALITY_AI.ERRORS.SAVE_QA'));
        this.savingEntry = false;
      },
    });
  }

  deleteEntry(entry: HospitalityQaEntryResponse): void {
    this.hospitalityAiService.deleteQaEntry(entry.id).subscribe({
      next: () => {
        this.message.success(this.translate.instant('HOSPITALITY_AI.QA_SECTION.DELETE_SUCCESS'));
        if (this.editingEntryId === entry.id) {
          this.startCreate();
        }
        this.loadEntries();
        this.loadMetrics();
      },
      error: (error) => {
        console.error('Failed to delete hospitality QA entry', error);
        this.message.error(this.translate.instant('HOSPITALITY_AI.ERRORS.DELETE_QA'));
      },
    });
  }

  trackEntry(_: number, entry: HospitalityQaEntryResponse): string {
    return entry.id;
  }

  onDownvotesQueryParamsChange(params: NzTableQueryParams): void {
    const { pageIndex, pageSize, sort } = params;
    let shouldReload = false;

    if (pageSize !== this.downvotesQuery.pageSize) {
      this.downvotesQuery = {
        ...this.downvotesQuery,
        pageSize,
        page: 1,
      };
      shouldReload = true;
    } else if (pageIndex !== this.downvotesQuery.page) {
      this.downvotesQuery = {
        ...this.downvotesQuery,
        page: pageIndex,
      };
      shouldReload = true;
    }

    const activeSort = sort.find((item) => item.value !== null);
    if (activeSort && activeSort.value) {
      const sortField = (activeSort.key as DownvoteSortField) || 'createdAt';
      const sortOrder = activeSort.value === 'ascend' ? 'asc' : 'desc';
      if (sortField !== this.downvotesQuery.sortField || sortOrder !== this.downvotesQuery.sortOrder) {
        this.downvotesQuery = {
          ...this.downvotesQuery,
          sortField,
          sortOrder,
          page: 1,
        };
        shouldReload = true;
      }
    } else if (this.downvotesQuery.sortField !== 'createdAt' || this.downvotesQuery.sortOrder !== 'desc') {
      this.downvotesQuery = { ...HospitalityAiInsightsComponent.DOWNVOTES_DEFAULT_STATE };
      shouldReload = true;
    }

    if (shouldReload) {
      this.loadMetrics();
    }
  }

  onTurnsQueryParamsChange(params: NzTableQueryParams): void {
    const { pageIndex, pageSize, sort } = params;
    let shouldReload = false;

    if (pageSize !== this.turnsQuery.pageSize) {
      this.turnsQuery = {
        ...this.turnsQuery,
        pageSize,
        page: 1,
      };
      shouldReload = true;
    } else if (pageIndex !== this.turnsQuery.page) {
      this.turnsQuery = {
        ...this.turnsQuery,
        page: pageIndex,
      };
      shouldReload = true;
    }

    const activeSort = sort.find((item) => item.value !== null);
    if (activeSort && activeSort.value) {
      const sortField = (activeSort.key as TurnSortField) || 'createdAt';
      const sortOrder = activeSort.value === 'ascend' ? 'asc' : 'desc';
      if (sortField !== this.turnsQuery.sortField || sortOrder !== this.turnsQuery.sortOrder) {
        this.turnsQuery = {
          ...this.turnsQuery,
          sortField,
          sortOrder,
          page: 1,
        };
        shouldReload = true;
      }
    } else if (this.turnsQuery.sortField !== 'createdAt' || this.turnsQuery.sortOrder !== 'desc') {
      this.turnsQuery = { ...HospitalityAiInsightsComponent.TURNS_DEFAULT_STATE };
      shouldReload = true;
    }

    if (shouldReload) {
      this.loadMetrics();
    }
  }

  getSortOrder(columnKey: string, activeField: string, activeOrder: SortOrder): NzTableSortOrder | null {
    if (columnKey !== activeField) {
      return null;
    }
    return activeOrder === 'asc' ? 'ascend' : 'descend';
  }

  private resetRecentTables(): void {
    this.downvotesQuery = { ...HospitalityAiInsightsComponent.DOWNVOTES_DEFAULT_STATE };
    this.turnsQuery = { ...HospitalityAiInsightsComponent.TURNS_DEFAULT_STATE };
  }

  private resetQaSort(): void {
    this.qaSort = { ...HospitalityAiInsightsComponent.QA_DEFAULT_SORT };
  }

  private createMessagePreview(text: string, limit = 160): string {
    if (text.length <= limit) {
      return text;
    }
    const sliced = text.slice(0, limit).trimEnd();
    return `${sliced}…`;
  }
}

type SortOrder = 'asc' | 'desc';
type DownvoteSortField = 'createdAt' | 'language' | 'property';
type TurnSortField = 'createdAt' | 'language' | 'property';
type QaSortField = 'question' | 'property' | 'language' | 'isActive' | 'updatedAt' | 'createdAt';

interface DownvoteQueryState {
  page: number;
  pageSize: number;
  sortField: DownvoteSortField;
  sortOrder: SortOrder;
}

interface TurnQueryState {
  page: number;
  pageSize: number;
  sortField: TurnSortField;
  sortOrder: SortOrder;
}

interface QaSortState {
  sortField: QaSortField;
  sortOrder: SortOrder;
}
